import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import GoalWinAudit from '@/components/GoalWinAudit';
import SpeechToText from '@/components/SpeechToText';
import AppShell from '@/components/AppShell';
import { Plus, Sparkles, Zap, Flame, Clock } from 'lucide-react';
import FirstWinGoalNudge from '@/components/FirstWinGoalNudge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentWins, setRecentWins] = useState([]);
  const [goals, setGoals] = useState([]);
  const [showLogWin, setShowLogWin] = useState(false);
  const [winText, setWinText] = useState('');
  const [winCategory, setWinCategory] = useState([]);  // Changed to array
  const [winGoalId, setWinGoalId] = useState('');
  const [enhancedText, setEnhancedText] = useState(null);
  const [suggestedGoal, setSuggestedGoal] = useState(null);
  const [linkingReason, setLinkingReason] = useState('');
  const [linkedGoalName, setLinkedGoalName] = useState('');
  const [showAISuggestion, setShowAISuggestion] = useState(false);
  const [currentWinId, setCurrentWinId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [polishError, setPolishError] = useState(null);
  const [allCategories, setAllCategories] = useState([
    'Project Delivery',
    'Process Improvement',
    'Leadership',
    'Stakeholder Impact',
    'Problem Solving',
    'Learning & Growth'
  ]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [freeTierStatus, setFreeTierStatus] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [showFirstWinNudge, setShowFirstWinNudge] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const categories = [
    'Project Delivery',
    'Process Improvement',
    'Leadership',
    'Stakeholder Impact',
    'Problem Solving',
    'Learning & Growth'
  ];

  useEffect(() => {
    // Batch ALL API calls in one go, avoids sequential round trips to Render
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${sessionToken}` };

      const [userRes, statsRes, winsRes, goalsRes, freeTierRes, streakRes, catsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers }),
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/wins`, { headers }),
        axios.get(`${API}/goals`, { headers }),
        axios.get(`${API}/wins/free-tier-status`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/wins/streak`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/users/categories`, { headers }).catch(() => ({ data: { all: [] } })),
      ]);

      setUser(userRes.data);
      setStats(statsRes.data);
      const winsData = winsRes.data.wins || winsRes.data;
      setRecentWins(Array.isArray(winsData) ? winsData.slice(0, 5) : []);
      setGoals(Array.isArray(goalsRes.data) ? goalsRes.data : []);
      setFreeTierStatus(freeTierRes.data);
      setStreakData(streakRes.data);
      setAllCategories(catsRes.data.all || categories);
    } catch (error) {
      console.error('Dashboard load error:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
        navigate('/login');
      }
    } finally {
      setIsInitialLoading(false);
    }
  };

  // Calculate weeks until user's next review
  const getReviewCountdown = () => {
    const reviewMonth = user?.next_review_month;
    if (!reviewMonth) return null; // User hasn't set their review month

    const now = new Date();
    const year = now.getFullYear();
    // reviewMonth is 1-12, JS months are 0-11
    let reviewDate = new Date(year, reviewMonth - 1, 1);
    // If review month already passed this year, use next year
    if (reviewDate <= now) {
      reviewDate = new Date(year + 1, reviewMonth - 1, 1);
    }
    const diffMs = reviewDate - now;
    const diffWeeks = Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000));
    return diffWeeks;
  };

  const fetchCategories = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/users/categories`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setAllCategories(response.data.all || categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setAllCategories(categories);
    }
  };

  const handleAddCustomCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(
        `${API}/users/custom-categories`,
        { category_name: newCategoryName },
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );

      toast.success('Category added');
      setNewCategoryName('');
      setShowAddCategory(false);
      fetchCategories();
    } catch (error) {
      console.error('Failed to add category:', error);
      toast.error('Failed to add category');
    }
  };

  const fetchData = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${sessionToken}` };

      const [userRes, statsRes, winsRes, goalsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers }),
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/wins`, { headers }),
        axios.get(`${API}/goals`, { headers })
      ]);

      setUser(userRes.data);
      setStats(statsRes.data);

      // Handle paginated wins response
      const winsData = winsRes.data.wins || winsRes.data;
      setRecentWins(Array.isArray(winsData) ? winsData.slice(0, 5) : []);

      // Goals are now returned directly as array (from aggregation)
      setGoals(Array.isArray(goalsRes.data) ? goalsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
        navigate('/login');
      }
    }
  };

  const handleLogWin = async () => {
    if (!winText.trim()) {
      setPolishError('Type in what you accomplished first.');
      return;
    }

    // Check free tier limit before submitting
    if (freeTierStatus?.is_free_tier && freeTierStatus.wins_remaining <= 0) {
      setPolishError('You have used all your free wins this month. Upgrade to keep logging.');
      return;
    }

    setIsSubmitting(true);
    setPolishError(null);
    try {
      const sessionToken = localStorage.getItem('session_token');

      const response = await axios.post(
        `${API}/wins`,
        {
          original_text: winText,
          category: Array.isArray(winCategory) ? winCategory : (winCategory ? [winCategory] : []),
          goal_id: winGoalId === 'none' || !winGoalId ? null : winGoalId,
          win_date: new Date().toISOString()
        },
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );

      // Every user gets the AI polish. Free-tier volume caps still apply
      // (backend enforces them on POST /api/wins and returns 403 when the
      // monthly quota is exhausted), but the polish itself is the core
      // product and every signup sees it.
      //
      // First-win nudge still fires once for a brand-new user.
      if (recentWins.length === 0 && !localStorage.getItem('bragbox_first_win_nudge_shown')) {
        setShowFirstWinNudge(true);
      }

      setEnhancedText(response.data.enhanced_text);
      setSuggestedGoal(response.data.suggested_goal_id);
      setLinkingReason(response.data.linking_reason);
      setLinkedGoalName(response.data.linked_goal_name);
      setCurrentWinId(response.data.win_id);
      setShowAISuggestion(true);
    } catch (error) {
      console.error('Failed to log win:', error);
      if (error.response?.status === 403) {
        setPolishError(error.response.data?.detail || 'You have used all your free wins this month. Upgrade to keep logging.');
      } else {
        setPolishError("We couldn't polish this win. Give it another try in a moment.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptWin = async (useEnhanced) => {
    try {
      // Update the win to use enhanced version if selected
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/wins/${currentWinId}/enhance`,
        { win_id: currentWinId, use_enhanced: useEnhanced },
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );

      toast.success(useEnhanced ? 'Saved the polished version' : 'Saved your original wording');
    } catch (error) {
      console.error('Failed to update win:', error);
    }

    // First win nudge for non-free-tier users
    if (recentWins.length === 0 && !localStorage.getItem('bragbox_first_win_nudge_shown')) {
      setShowFirstWinNudge(true);
    }
    setShowLogWin(false);
    setWinText('');
    setWinCategory([]);
    setWinGoalId('');
    setEnhancedText(null);
    setSuggestedGoal(null);
    setLinkingReason('');
    setLinkedGoalName('');
    setShowAISuggestion(false);
    setCurrentWinId(null);
    setPolishError(null);
    fetchData();
  };

  const handleDiscardWin = async () => {
    if (currentWinId) {
      // Delete the win if user discards
      try {
        const sessionToken = localStorage.getItem('session_token');
        await axios.delete(`${API}/wins/${currentWinId}`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
      } catch (error) {
        console.error('Failed to delete win:', error);
      }
    }

    setShowLogWin(false);
    setWinText('');
    setWinCategory([]);
    setWinGoalId('');
    setEnhancedText(null);
    setSuggestedGoal(null);
    setLinkingReason('');
    setLinkedGoalName('');
    setShowAISuggestion(false);
    setCurrentWinId(null);
    setPolishError(null);
    fetchData();
  };

  // Skeleton loader: shows immediately while data loads inside the shared shell
  if (isInitialLoading) {
    return (
      <AppShell>
        <div className="p-4 md:p-8 max-w-7xl mx-auto w-full">
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-4 w-64 bg-gray-100 rounded animate-pulse mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-white rounded-xl border border-gray-100 animate-pulse" />
            <div className="h-64 bg-white rounded-xl border border-gray-100 animate-pulse" />
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              {(() => {
                // Fix #1: First-session users (account < 1h old) see "Welcome", returning users see "Welcome back".
                // Backend returns created_at as a naive ISO string (e.g. "2026-04-29T11:06:43.761000").
                // JS parses naive strings as LOCAL time, but the backend stores UTC, so we append
                // 'Z' when no timezone marker is present. Without this fix, browsers in non-UTC zones
                // saw a skewed accountAgeMs and fell through to "Welcome back" on a brand-new account.
                const firstName = user?.name?.split(' ')[0] || 'there';
                const rawTs = user?.created_at;
                const tsAsUtc = rawTs && !/[Zz]|[+-]\d\d:?\d\d$/.test(rawTs) ? rawTs + 'Z' : rawTs;
                const accountAgeMs = tsAsUtc ? Date.now() - new Date(tsAsUtc).getTime() : Infinity;
                const isFirstSession = accountAgeMs < 60 * 60 * 1000;
                return isFirstSession ? `Welcome, ${firstName}!` : `Welcome back, ${firstName}!`;
              })()}
            </h2>
            <p className="text-gray-600">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          {/* Free Tier Banner */}
          {freeTierStatus?.is_free_tier && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="free-tier-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--navy-900)' }}>
                    Free Plan: {freeTierStatus.wins_remaining} of {freeTierStatus.monthly_limit} wins remaining this month
                  </p>
                  <p className="text-xs text-gray-500">When they run out, your streak stops.</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/pricing')}
                className="px-4 py-2 text-sm font-semibold shrink-0"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                data-testid="free-tier-upgrade-btn"
              >
                Keep my streak →
              </Button>
            </div>
          )}

          {/* Review Season Countdown + Streak */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Review Countdown */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-testid="review-countdown">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--cream-100)' }}>
                <Clock className="w-5 h-5" style={{ color: 'var(--navy-900)' }} />
              </div>
              {getReviewCountdown() !== null ? (
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--navy-900)' }}>
                    {getReviewCountdown()} weeks until your review
                  </p>
                  <p className="text-xs text-gray-500">Keep documenting. Future you will thank you.</p>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--navy-900)' }}>When's your next review?</p>
                    <p className="text-xs text-gray-500">Set it so we can keep you on track</p>
                  </div>
                  <select
                    className="ml-3 text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="review-month-select"
                    defaultValue=""
                    onChange={async (e) => {
                      const month = parseInt(e.target.value);
                      if (!month) return;
                      try {
                        const sessionToken = localStorage.getItem('session_token');
                        await axios.put(`${API}/users/profile`, { next_review_month: month }, {
                          headers: { 'Authorization': `Bearer ${sessionToken}` }
                        });
                        setUser(prev => ({ ...prev, next_review_month: month }));
                        toast.success('Review month saved');
                      } catch (err) {
                        toast.error('Failed to save');
                      }
                    }}
                  >
                    <option value="" disabled>Select month</option>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                      <option key={i+1} value={i+1}>{m}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Streak Counter */}
            {streakData && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-testid="streak-counter">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: streakData.streak_weeks > 0 ? '#FEF3C7' : 'var(--cream-100)' }}>
                  <Flame className="w-5 h-5" style={{ color: streakData.streak_weeks > 0 ? '#D97706' : '#9CA3AF' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--navy-900)' }}>
                    {streakData.streak_weeks > 0
                      ? `${streakData.streak_weeks} week streak, keep it going!`
                      : 'No streak yet, log a win to start!'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {streakData.has_logged_this_week
                      ? "You've logged a win this week"
                      : "Log a win this week to keep your streak alive"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <button
                onClick={() => navigate('/wins')}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-navy-900 hover:shadow-md transition-all duration-200 text-left cursor-pointer"
              >
                <div className="text-sm text-gray-600 mb-1">Wins This Month</div>
                <div className="text-3xl font-bold" style={{ color: 'var(--navy-900)' }}>
                  {stats.wins_this_month}
                </div>
                <div className="text-xs text-gray-500 mt-2">View all wins →</div>
              </button>

              <button
                onClick={() => navigate('/wins')}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-navy-900 hover:shadow-md transition-all duration-200 text-left cursor-pointer"
              >
                <div className="text-sm text-gray-600 mb-1">Wins This Quarter</div>
                <div className="text-3xl font-bold" style={{ color: 'var(--navy-900)' }}>
                  {stats.wins_this_quarter}
                </div>
                <div className="text-xs text-gray-500 mt-2">View all wins →</div>
              </button>

              <button
                onClick={() => navigate('/goals')}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-navy-900 hover:shadow-md transition-all duration-200 text-left cursor-pointer"
              >
                <div className="text-sm text-gray-600 mb-1">Active Goals</div>
                <div className="text-3xl font-bold" style={{ color: 'var(--navy-900)' }}>
                  {stats.total_goals}
                </div>
                <div className="text-xs text-gray-500 mt-2">Manage goals →</div>
              </button>
            </div>
          )}

          {/* Primary Action */}
          <div className="mb-8">
            <Button
              data-testid="log-win-btn"
              onClick={() => setShowLogWin(true)}
              className="w-full p-8 text-lg font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-3"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              <Plus className="w-6 h-6" />
              Log a Win
            </Button>
          </div>

          {/* Recent Wins */}
          {recentWins.length > 0 ? (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                  Recent Wins
                </h3>
                <Link to="/wins" className="text-sm" style={{ color: 'var(--navy-600)' }}>
                  View all →
                </Link>
              </div>
              <div className="space-y-3">
                {recentWins.map((win) => (
                  <div key={win.win_id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-navy-200 transition-colors duration-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex flex-wrap gap-2">
                        {Array.isArray(win.category) ? (
                          win.category.map((cat, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-1 rounded"
                              style={{ backgroundColor: 'var(--cream-100)', color: 'var(--navy-900)' }}
                            >
                              {cat}
                            </span>
                          ))
                        ) : win.category ? (
                          <span
                            className="text-xs px-2 py-1 rounded"
                            style={{ backgroundColor: 'var(--cream-100)', color: 'var(--navy-900)' }}
                          >
                            {win.category}
                          </span>
                        ) : null}
                      </div>
                      <span className="text-xs text-gray-500" style={{ fontFamily: 'JetBrains Mono' }}>
                        {new Date(win.win_date).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed break-words whitespace-normal">
                      {win.use_enhanced && win.enhanced_text ? win.enhanced_text : win.original_text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-8 bg-white border-2 border-dashed border-gray-200 rounded-xl p-10 text-center" data-testid="empty-state-wins">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--cream-100)' }}>
                <Sparkles className="w-8 h-8" style={{ color: 'var(--navy-900)' }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                Your first win takes 30 seconds
              </h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                Your next review will thank you. Start documenting what you've accomplished, big or small.
              </p>
              <Button
                onClick={() => setShowLogWin(true)}
                className="px-8 py-6 text-base font-semibold"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                data-testid="empty-state-log-win-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Log Your First Win
              </Button>
            </div>
          )}
          {goals.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                  Goals Progress
                </h3>
                <GoalWinAudit onRefresh={fetchData} />
              </div>
              <div className="space-y-3">
                {goals.slice(0, 3).map((goal) => (
                  <div key={goal.goal_id} className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium" style={{ color: 'var(--navy-900)' }}>{goal.title}</h4>
                      <span className="text-sm font-medium">{goal.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{ width: `${goal.progress}%`, backgroundColor: 'var(--navy-900)' }}
                      />
                    </div>
                    {goal.win_count > 0 && (
                      <p className="text-xs text-gray-600 mt-2">{goal.win_count} wins linked</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Log Win Dialog */}
          <Dialog open={showLogWin} onOpenChange={(open) => { setShowLogWin(open); if (!open) setPolishError(null); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                  {showAISuggestion ? 'Your polished win' : 'Log a Win'}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {!showAISuggestion ? (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium" style={{ color: 'var(--navy-900)' }}>
                          What did you accomplish?
                        </label>
                        <SpeechToText
                          onTranscript={(text) => setWinText(prev => prev + (prev ? ' ' : '') + text)}
                          buttonStyle="icon"
                        />
                      </div>
                      <Textarea
                        data-testid="win-text-input"
                        value={winText}
                        onChange={(e) => setWinText(e.target.value)}
                        placeholder="e.g., Caught a $12K billing error before it went to leadership"
                        className="min-h-32"
                      />
                    </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                    Category (select all that apply)
                  </label>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {allCategories.map((cat) => (
                      <label
                        key={cat}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          winCategory.includes(cat)
                            ? 'bg-navy-50'
                            : 'border-gray-200 hover:border-navy-200'
                        }`}
                        style={winCategory.includes(cat) ? { borderColor: 'var(--navy-900)' } : {}}
                      >
                        <input
                          type="checkbox"
                          checked={winCategory.includes(cat)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWinCategory([...winCategory, cat]);
                            } else {
                              setWinCategory(winCategory.filter(c => c !== cat));
                            }
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm">{cat}</span>
                      </label>
                    ))}
                  </div>

                  {!showAddCategory ? (
                    <Button
                      onClick={() => setShowAddCategory(true)}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      + Add Custom Category
                    </Button>
                  ) : (
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Enter custom category..."
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleAddCustomCategory}
                        size="sm"
                        style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                      >
                        Add
                      </Button>
                      <Button
                        onClick={() => {
                          setShowAddCategory(false);
                          setNewCategoryName('');
                        }}
                        variant="outline"
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                        Link to Goal (optional - AI will suggest if left blank)
                      </label>
                      <Select value={winGoalId} onValueChange={setWinGoalId}>
                        <SelectTrigger data-testid="win-goal-select">
                          <SelectValue placeholder="Let AI suggest a goal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {goals.map((goal) => (
                            <SelectItem key={goal.goal_id} value={goal.goal_id}>{goal.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {polishError && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="polish-error">
                        <p className="text-sm text-red-700">{polishError}</p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button
                        data-testid="save-win-btn"
                        onClick={handleLogWin}
                        disabled={isSubmitting}
                        className="flex-1 py-3 rounded-lg font-medium transition-colors duration-200"
                        style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                      >
                        {isSubmitting ? 'Polishing your win...' : 'Polish'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDiscardWin}
                        className="px-6"
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg border border-gray-200">
                        <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-2">Original</p>
                        <p className="text-sm text-gray-700">{winText}</p>
                      </div>

                      {enhancedText && (
                        <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--cream-100)', borderLeft: '4px solid var(--navy-900)' }}>
                          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: 'var(--navy-900)' }}>Polished version</p>
                          <p className="text-sm text-gray-700">{enhancedText}</p>
                        </div>
                      )}

                      {suggestedGoal && linkingReason && (
                        <div className="p-4 rounded-lg border-2" style={{ borderColor: 'var(--navy-200)', backgroundColor: 'var(--navy-50)' }}>
                          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: 'var(--navy-900)' }}>Suggested goal</p>
                          <p className="text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                            Linked to: {linkedGoalName}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Why:</strong> {linkingReason}
                          </p>
                        </div>
                      )}

                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-sm font-medium mb-3" style={{ color: 'var(--navy-900)' }}>
                          Which version would you like to keep?
                        </p>
                        <div className="flex gap-3">
                          <Button
                            onClick={() => handleAcceptWin(true)}
                            className="flex-1 py-3 rounded-lg font-medium transition-colors duration-200"
                            style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                          >
                            Save polished version
                          </Button>
                          <Button
                            onClick={() => handleAcceptWin(false)}
                            variant="outline"
                            className="flex-1 py-3"
                          >
                            Keep my wording
                          </Button>
                        </div>
                        <div className="flex gap-3 mt-2">
                          {enhancedText && (
                            <Button
                              onClick={() => {
                                navigator.clipboard.writeText(enhancedText);
                                toast.success('Copied to clipboard');
                              }}
                              variant="ghost"
                              className="flex-1"
                            >
                              Copy polished text
                            </Button>
                          )}
                          <Button
                            onClick={handleDiscardWin}
                            variant="ghost"
                            className="flex-1"
                          >
                            Discard win
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

      {/* First Win Goal Nudge Modal */}
      <FirstWinGoalNudge
        open={showFirstWinNudge}
        onClose={() => {
          setShowFirstWinNudge(false);
          localStorage.setItem('bragbox_first_win_nudge_shown', 'true');
        }}
      />
    </AppShell>
  );
};

export default Dashboard;
